<template>
  <div class="contract-specials">
    <div class="d-flex align-center mb-4">
      <span class="text-h6">{{ isInsuredMode ? "Особые условия" : "Особые условия контракта" }}</span>
      <v-spacer></v-spacer>
      <v-btn
        v-if="canWrite"
        outlined
        color="primary"
        :disabled="!ownerId"
        @click="add"
      >
        <v-icon left>add</v-icon>
        Добавить условие
      </v-btn>
    </div>

    <div v-if="!ownerId" class="grey--text">
      {{ isInsuredMode ? "Особые условия можно добавить после сохранения застрахованного" : "Особые условия можно добавить после сохранения контракта" }}
    </div>
    <div v-else-if="!loading && !blocks.length" class="grey--text">
      Особых условий нет
    </div>

    <v-card
      v-for="block in blocks"
      :key="block.uid"
      outlined
      class="mb-4"
      :class="{ 'special-highlighted': block.isHighlighted }"
    >
      <v-card-title class="py-2">
        <v-text-field
          v-if="isEditable(block)"
          v-model="block.header"
          label="Заголовок"
          class="mr-4"
          dense
          hide-details
        ></v-text-field>
        <span v-else class="subtitle-1 font-weight-bold special-header">{{ block.header }}</span>
        <v-spacer v-if="!isEditable(block)"></v-spacer>
        <v-chip v-if="isInsuredMode" x-small label class="mr-2">
          {{ block.own ? "Условие застрахованного" : "Условие контракта" }}
        </v-chip>
        <v-btn v-if="isEditable(block)" text small color="primary" :loading="block.saving" @click="save(block)">
          <v-icon left small>save</v-icon>
          Сохранить
        </v-btn>
        <v-btn
          v-if="block.own && (canDelete || (canWrite && !block.id))"
          text
          small
          color="error"
          :disabled="block.saving"
          @click="askDelete(block)"
        >
          <v-icon left small>delete</v-icon>
          Удалить
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-textarea
          v-model="block.text"
          label="Текст"
          :readonly="!isEditable(block)"
          auto-grow
          rows="3"
          outlined
          hide-details
        ></v-textarea>
        <div class="d-flex">
          <v-checkbox
            v-model="block.isBotInfo"
            label="Использовать в боте"
            class="mr-6"
            :readonly="!isEditable(block)"
            dense
            hide-details
          ></v-checkbox>
          <v-checkbox
            v-model="block.isHighlighted"
            label="Выделять"
            color="red"
            :readonly="!isEditable(block)"
            dense
            hide-details
          ></v-checkbox>
        </div>
      </v-card-text>
    </v-card>

    <DeleteConfirmation
      :show="!!blockToDelete"
      prompt-text="Удалить особое условие?"
      @deletionAgreed="confirmDelete"
      @deletionCanceled="blockToDelete = null"
    ></DeleteConfirmation>
  </div>
</template>

<script>
import ctx from "@/ctx";
import fetch from "@/utils/fetch";
import jwtParser from "@/utils/jwtParser";
import storage from "@/utils/storage";
import DeleteConfirmation from "@/components/common/DeleteConfirmation";

const ACCESS = "CONTRACT_SPECIAL";
const API = "/api/insurance/contract-special";

let uidSeq = 0;

const toBlock = (dto, own) => ({
  uid: ++uidSeq,
  id: dto.id || null,
  header: dto.header || "",
  text: dto.text || "",
  isBotInfo: !!dto.isBotInfo,
  isHighlighted: !!dto.isHighlighted,
  own,
  saving: false
});

const asList = res => (Array.isArray(res) ? res : []);

/**
 * Без insuredId — условия контракта (создаются в контракте).
 * С insuredId — условия контракта застрахованного (только чтение) + собственные условия застрахованного.
 */
export default {
  name: "ContractSpecials",
  props: {
    contractId: [Number, String],
    insuredId: [Number, String]
  },
  data: () => ({
    blocks: [],
    loading: false,
    blockToDelete: null
  }),
  computed: {
    isInsuredMode() {
      return this.insuredId !== undefined;
    },
    ownerId() {
      return this.isInsuredMode ? this.insuredId : this.contractId;
    },
    loadKey() {
      return `${this.contractId}|${this.insuredId}`;
    },
    rights() {
      const access = jwtParser.parse(storage.getJwt())?.access;
      const entry = Array.isArray(access) ? access.find(a => a && a.access === ACCESS) : null;
      return entry?.rights || "";
    },
    canWrite() {
      return this.rights.includes("w");
    },
    canDelete() {
      return this.rights.includes("d");
    }
  },
  watch: {
    loadKey: {
      immediate: true,
      handler() {
        this.load();
      }
    }
  },
  methods: {
    isEditable(block) {
      return this.canWrite && block.own;
    },
    async load() {
      this.blocks = [];
      const key = this.loadKey;
      const { contractId, insuredId } = this;
      this.loading = true;
      try {
        const [contractRes, insuredRes] = await Promise.all([
          contractId ? fetch.get(`${API}/by-contract?contractId=${contractId}`) : null,
          this.isInsuredMode && insuredId ? fetch.get(`${API}/by-insured?insuredId=${insuredId}`) : null
        ]);
        if (key !== this.loadKey) return;
        this.blocks = [
          ...asList(contractRes).map(dto => toBlock(dto, !this.isInsuredMode)),
          ...asList(insuredRes).map(dto => toBlock(dto, true))
        ];
      } finally {
        this.loading = false;
      }
    },
    add() {
      this.blocks.push(toBlock({}, true));
    },
    async save(block) {
      if (!block.header.trim() || !block.text.trim()) {
        ctx.system.msg = "[Ошибка]: заполните заголовок и текст";
        return;
      }
      block.saving = true;
      try {
        const res = await fetch.post(`${API}/save`, {
          id: block.id,
          contractId: this.isInsuredMode ? null : this.contractId,
          insuredId: this.isInsuredMode ? this.insuredId : null,
          header: block.header,
          text: block.text,
          isBotInfo: block.isBotInfo,
          isHighlighted: block.isHighlighted
        });
        if (res && res.id) {
          block.id = res.id;
          block.header = res.header;
          block.text = res.text;
          block.isBotInfo = !!res.isBotInfo;
          block.isHighlighted = !!res.isHighlighted;
          ctx.system.msg = "Особое условие сохранено";
        }
      } finally {
        block.saving = false;
      }
    },
    askDelete(block) {
      if (!block.id) {
        this.remove(block);
        return;
      }
      this.blockToDelete = block;
    },
    async confirmDelete() {
      const block = this.blockToDelete;
      this.blockToDelete = null;
      const res = await fetch.post(`${API}/delete?id=${block.id}`, null);
      if (res) this.remove(block);
    },
    remove(block) {
      this.blocks = this.blocks.filter(b => b !== block);
    }
  },
  components: {
    DeleteConfirmation
  }
};
</script>

<style lang="scss" scoped>
.special-highlighted {
  border: 2px solid #e53935 !important;

  .special-header,
  ::v-deep textarea,
  ::v-deep .v-text-field input {
    color: #e53935 !important;
  }
}
</style>
